import React, { useEffect, useRef } from "react";
import {
  ArrowRight,
  ArrowLeft,
  User,
  Sparkles,
  Activity,
  Users,
  Search,
  LayoutGrid,
  Settings,
  Share2,
  BarChart3,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useHomeCta } from "../hooks/useHomeCta";
import { openAgent } from "../utils/openAgent";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const LOOP_PILLARS = [
  {
    icon: User,
    iconBg: "bg-[#E0F2FE]/80",
    iconColor: "text-[#0284C7]",
    title: "Business-led",
    desc: "Focused on real business outcomes.",
  },
  {
    icon: Sparkles,
    iconBg: "bg-[#F5D0FE]/80",
    iconColor: "text-[#C026D3]",
    title: "AI-powered",
    desc: "Combines domain expertise with advanced AI.",
  },
  {
    icon: Activity,
    iconBg: "bg-[#DCFCE7]/90",
    iconColor: "text-[#16A34A]",
    title: "Continuous",
    desc: "Designed for long-term value, not one-time projects.",
  },
];

const SIX_STAGES = [
  {
    num: "01",
    icon: Users,
    title: "Understand",
    text: "Learn your business in context - goals, challenges, systems and data.",
  },
  {
    num: "02",
    icon: Search,
    title: "Discover",
    text: "Identify high-value opportunities where AI can create measurable impact.",
  },
  {
    num: "03",
    icon: LayoutGrid,
    title: "Prioritize",
    text: "Evaluate and focus on the opportunities that matter most.",
  },
  {
    num: "04",
    icon: Settings,
    title: "Build",
    text: "Design, develop and validate AI solutions tailored to your business.",
  },
  {
    num: "05",
    icon: Share2,
    title: "Integrate",
    text: "Embed solutions into your existing systems and workflows.",
  },
  {
    num: "06",
    icon: BarChart3,
    title: "Measure & Evolve",
    text: "Track Outcomes, learn from insights and continuously scale new value.",
  },
];

const PRACTICE_STEPS = [
  {
    num: "1",
    color: "#0099FF",
    titleLine1: "Assess",
    titleLine2: "Current State",
  },
  {
    num: "2",
    color: "#7C3AED",
    titleLine1: "Identify",
    titleLine2: "Opportunities",
  },
  {
    num: "3",
    color: "#D97706",
    titleLine1: "Build and",
    titleLine2: "implement",
  },
  {
    num: "4",
    color: "#0D9488",
    titleLine1: "Measure",
    titleLine2: "business impact",
  },
  {
    num: "5",
    color: "#0284C7",
    titleLine1: "Scale and create",
    titleLine2: "new value",
  },
];

const CASE_STUDIES = [
  {
    id: "aptia",
    client: "Aptia",
    category: "Enterprise platform & transformation",
    image: "/images/aptia-case-study.png",
    imageBg: "bg-[#0A2533]",
    desc: "We helped Aptia modernize its technology platforms and process with AI, delivering greater efficiency, scalability and customer value across the business.",
    metrics: [
      { value: "40%", label: "Faster Process" },
      { value: "30%", label: "Cost Reduction" },
      { value: "2x", label: "Customer Satisfaction" },
    ],
  },
  {
    id: "skipper",
    client: "Skipper Ltd.",
    category: "Digitizing manufacturing Operations",
    image: "/images/skipper-case-study.jpg",
    imageBg: "bg-[#114C77]",
    desc: "We built digital solutions to streamline galvanizing and tower manufacturing processes, enabling better visibility and efficiency.",
    metrics: [
      { value: "25%", label: "Reduction in Downtime" },
      { value: "18%", label: "Increase in Production" },
      { value: "30%", label: "Improved Operations" },
    ],
  },
  {
    id: "retail",
    client: "Leading Retail Chain",
    category: "Personalized Customer experiences",
    image: "/images/retail-case-study.jpg",
    imageBg: "bg-slate-900",
    desc: "We implement AI-driven analytics to understand customer behavior, personalize engagement and optimize inventory.",
    metrics: [
      { value: "40%", label: "Faster Process" },
      { value: "30%", label: "Cost Reduction" },
      { value: "2x", label: "Customer Satisfaction" },
    ],
  },
];

const SimpelLoop = () => {
  const goToHomeCta = useHomeCta();
  const navigate = useNavigate();
  const sliderRef = useRef(null);

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = direction === "left" ? -350 : 350;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="min-h-screen w-full text-white flex flex-col justify-between selection:bg-[#2099EC]/30 selection:text-white"
      style={{
        backgroundColor: "rgba(2, 17, 28, 1)",
      }}
    >
      <Navbar />

      {/* HERO / LOOP SECTION */}
      <main
        className="relative w-full flex-1 flex items-center overflow-hidden mt-[76px] sm:mt-[80px] py-12 lg:py-20"
        style={{
          backgroundColor: "rgba(2, 17, 28, 1)",
        }}
      >
        {/* Top-left glow under navbar on the left side only */}
        <div
          className="pointer-events-none absolute left-0 top-0 w-[520px] h-[260px] opacity-75 blur-[70px]"
          style={{
            background:
              "radial-gradient(ellipse at 15% 0%, rgba(23, 56, 98, 1) 0%, rgba(23, 56, 98, 0.45) 50%, rgba(2, 17, 28, 0) 80%)",
          }}
        />

        <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
            {/* Left Column: Heading & Description matching screenshot */}
            <div className="flex flex-col items-start gap-5 lg:col-span-5">
              {/* Tag / Eyebrow */}
              <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#808A95]">
                OUR APPROCH
              </p>

              {/* Main Headline */}
              <h1 className="text-[38px] sm:text-[46px] lg:text-[52px] xl:text-[58px] font-bold leading-[1.08] tracking-tight text-[#F5F6F6]">
                Simpel <span className="text-[#2099EC]">LOOP™</span>
              </h1>

              {/* Subheading */}
              <h2 className="text-[18px] sm:text-[20px] lg:text-[22px] font-semibold leading-snug text-[#D9DBDD]">
                A continuous cycle of business transformation.
              </h2>

              {/* Description Paragraph */}
              <p className="max-w-[490px] text-[14.5px] sm:text-[15px] leading-[25px] text-[#A2A7AC]">
                Simpel Loop™ connects your goals, business, systems and data to
                identify and deliver the right AI opportunities and keep creating
                value over time.
              </p>

              {/* Call to Action Button with glowing cyan drop shadow */}
              <div className="pt-2">
                <button
                  onClick={openAgent}
                  className="group flex items-center gap-3 rounded-full bg-white px-8 py-3.5 text-[15px] font-bold text-[#071B34] transition-all duration-300 hover:scale-[1.03] hover:bg-[#F8FAFC]"
                  style={{
                    boxShadow:
                      "0 10px 30px -5px rgba(32, 153, 236, 0.6), 0 0 20px rgba(32, 153, 236, 0.35)",
                  }}
                >
                  <span>Talk to Our Team</span>
                  <ArrowRight
                    className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                    strokeWidth={2.2}
                  />
                </button>
              </div>
            </div>

            {/* Right Column: High-Res Loop PNG matching screenshot */}
            <div className="flex w-full items-center justify-center lg:col-span-7 lg:justify-end">
              <div className="relative w-full max-w-[850px]">
                <img
                  src="/images/simpel-loop-transparent.png"
                  alt="Simpel LOOP™ continuous cycle of business transformation"
                  loading="eager"
                  fetchPriority="high"
                  className="w-full object-contain drop-shadow-[0_10px_35px_rgba(0,210,238,0.25)] select-none"
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* SECTION 1: WHAT IS SIMPEL LOOP™ */}
      <section className="w-full bg-white text-[#0F172A] py-16 lg:py-24 border-b border-[#F1F5F9]">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 flex flex-col gap-4">
              <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                WHAT IS SIMPEL LOOP™
              </p>
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold leading-[1.15] text-[#0B132B]">
                From opportunity to ongoing impact.
              </h2>
              <p className="max-w-[620px] text-[15px] sm:text-[16px] leading-[26px] text-[#64748B]">
                Simpel Loop™ is our end-to-end approach to help organizations
                discover, design, build, integrate and scale solutions. It is a
                continuous cycle that keeps your business moving forward.
              </p>
            </div>

            {/* Right Pillars with vertical divider on desktop */}
            <div className="lg:col-span-5 lg:border-l lg:border-[#E2E8F0] lg:pl-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {LOOP_PILLARS.map((p) => {
                const IconComponent = p.icon;
                return (
                  <div key={p.title} className="flex flex-col items-start">
                    <div
                      className={`flex h-12 w-12 items-center justify-center rounded-full ${p.iconBg} ${p.iconColor}`}
                    >
                      <IconComponent className="h-5 w-5" strokeWidth={2.2} />
                    </div>
                    <span className="text-[15px] font-bold text-[#0B132B] mt-4">
                      {p.title}
                    </span>
                    <span className="text-[12px] leading-[18px] text-[#64748B] mt-1.5">
                      {p.desc}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: THE SIX STAGES OF SIMPEL LOOP™ */}
      <section className="w-full bg-[#F4F8FC] text-[#0F172A] py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header */}
          <div className="flex flex-col gap-3 mb-12 sm:mb-16">
            <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
              THE SIX STAGES OF SIMPEL LOOP™
            </p>
            <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold leading-[1.15] text-[#0B132B]">
              A clear path from insight to impact.
            </h2>
            <p className="text-[15px] sm:text-[16px] leading-[26px] text-[#64748B] max-w-[760px]">
              Each stage builds on the previous one, creating a continuous cycle of
              improvement and value creation.
            </p>
          </div>

          {/* 6 Stage Cards connected by arrows */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:flex xl:items-stretch xl:justify-between gap-4">
            {SIX_STAGES.map((s, i) => {
              const IconComp = s.icon;
              return (
                <React.Fragment key={s.num}>
                  <div className="relative flex w-full flex-1 flex-col justify-start rounded-[20px] border border-[#E2E8F0]/80 bg-white p-6 shadow-[0_4px_25px_rgba(0,0,0,0.03)] transition-all duration-300 hover:-translate-y-1 hover:shadow-lg min-h-[250px] overflow-hidden">
                    {/* Top-Right Number Pill Ribbon */}
                    <div className="absolute top-0 right-0 rounded-bl-[14px] bg-[#00A3FF] px-3 py-1 text-[11px] font-bold text-white tracking-wide">
                      {s.num}
                    </div>

                    {/* Left Icon Badge */}
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#E0F2FE]/80 text-[#0284C7]">
                      <IconComp className="h-5 w-5" strokeWidth={2} />
                    </div>

                    {/* Content */}
                    <div className="flex flex-col mt-4">
                      <span className="text-[16px] font-bold leading-snug text-[#0B132B]">
                        {s.title}
                      </span>
                      <span className="text-[12.5px] font-normal leading-[20px] text-[#64748B] mt-2">
                        {s.text}
                      </span>
                    </div>
                  </div>

                  {/* Arrow between cards on desktop */}
                  {i < SIX_STAGES.length - 1 && (
                    <div className="hidden shrink-0 items-center justify-center px-0.5 xl:flex">
                      <ArrowRight
                        className="h-5 w-5 text-[#475569]"
                        strokeWidth={2.2}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 3: HOW IT WORKS IN PRACTICE */}
      <section className="relative w-full bg-[#020B16] text-white py-20 lg:py-28 overflow-hidden border-t border-white/5">
        {/* Low-opacity ambient background image */}
        <div className="pointer-events-none absolute inset-0 select-none overflow-hidden">
          <img
            src="/images/how-it-works-bg.png"
            alt=""
            className="w-full h-full object-cover object-center opacity-25 mix-blend-screen"
          />
        </div>

        <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          {/* Top Header Row */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 pb-16 lg:pb-24">
            {/* Left Headline */}
            <div className="flex flex-col gap-3 max-w-[620px]">
              <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
                HOW IT WORKS IN PRACTICE
              </p>
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold leading-[1.18] text-white">
                A continuous journey, not a<br className="hidden sm:inline" /> one-time project.
              </h2>
            </div>

            {/* Vertical Divider & Right Description */}
            <div className="flex items-center">
              <div className="h-16 w-[1px] bg-white/20 hidden lg:block mr-10" />
              <p className="text-[14px] sm:text-[15px] leading-[24px] text-[#CBD5E1] max-w-[480px]">
                Simpel Loop™ turns opportunities into measurable business outcomes- and keeps your organization ahead as new possibilities emerge.
              </p>
            </div>
          </div>

          {/* 5-Step Process Flow with Connecting Arrows */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:items-start lg:justify-between gap-8 lg:gap-2 pt-4">
            {PRACTICE_STEPS.map((step, idx) => (
              <React.Fragment key={step.num}>
                <div className="flex flex-col items-center text-center group flex-1">
                  {/* Number Circle Node */}
                  <div
                    className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full font-bold text-white text-[18px] sm:text-[20px] transition-transform duration-300 group-hover:scale-110"
                    style={{
                      backgroundColor: step.color,
                    }}
                  >
                    {step.num}
                  </div>

                  {/* Step Text Label */}
                  <div className="flex flex-col mt-4 min-h-[44px]">
                    <span className="text-[14px] sm:text-[15px] font-semibold text-white leading-tight">
                      {step.titleLine1}
                    </span>
                    <span className="text-[14px] sm:text-[15px] font-semibold text-white leading-tight mt-0.5">
                      {step.titleLine2}
                    </span>
                  </div>
                </div>

                {/* Arrow Connector between steps */}
                {idx < PRACTICE_STEPS.length - 1 && (
                  <div className="hidden lg:flex items-center justify-center px-1 mt-[22px]">
                    <ArrowRight className="h-5 w-5 text-white/35" strokeWidth={2} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: AI IN ACTION - CASE STUDIES */}
      <section className="relative w-full bg-[#F4F8FC] py-20 lg:py-28 overflow-hidden text-[#0F172A] border-t border-[#E2E8F0]/60">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
            {/* Left Header Column */}
            <div className="lg:col-span-4 flex flex-col justify-between pt-2">
              <div className="flex flex-col gap-3">
                <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                  A CLOSER LOOK
                </p>
                <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold leading-[1.15] text-[#0B132B]">
                  AI in action: Real solutions, real results
                </h2>
                <p className="text-[15px] sm:text-[16px] leading-[26px] text-[#64748B] mt-1">
                  See how we've helped businesses across industries solve complex challenges with AI.
                </p>
              </div>

              <div className="mt-8">
                <button
                  type="button"
                  onClick={() => {
                    window.scrollTo(0, 0);
                    navigate("/case-studies");
                  }}
                  className="inline-flex items-center gap-2 text-[15px] font-bold text-[#00A3FF] hover:text-[#0284C7] transition-colors group cursor-pointer"
                >
                  <span>View Case Studies</span>
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    strokeWidth={2.5}
                  />
                </button>
              </div>
            </div>

            {/* Right Cards Slider Column */}
            <div className="lg:col-span-8 flex flex-col min-w-0">
              {/* Cards Row (Horizontally scrollable) */}
              <div
                ref={sliderRef}
                className="flex gap-5 overflow-x-auto pb-4 scroll-smooth no-scrollbar"
                style={{
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {CASE_STUDIES.map((cs) => (
                  <div
                    key={cs.id}
                    className="w-[290px] sm:w-[325px] shrink-0 rounded-[22px] bg-white border border-[#E2E8F0]/80 shadow-[0_4px_25px_rgba(0,0,0,0.04)] hover:shadow-xl transition-all duration-300 flex flex-col justify-between overflow-hidden group"
                  >
                    {/* Top Image Banner with bottom fade */}
                    <div className={`relative h-[155px] w-full overflow-hidden ${cs.imageBg}`}>
                      <img
                        src={cs.image}
                        alt={cs.client}
                        className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                      {/* Gradient fade to white */}
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent" />
                    </div>

                    {/* Card Title & Category */}
                    <div className="flex items-center justify-between px-5 pt-3.5 pb-2">
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0B132B] whitespace-nowrap">
                        {cs.client}
                      </h3>
                      <div className="mx-3 h-4 w-[1px] bg-[#CBD5E1] shrink-0" />
                      <span className="text-[12px] font-medium leading-[15px] text-[#475569] text-right line-clamp-2">
                        {cs.category}
                      </span>
                    </div>

                    {/* Description Paragraph */}
                    <div className="px-5 pb-5 pt-1">
                      <p className="min-h-[66px] text-[13px] leading-[21px] text-[#64748B]">
                        {cs.desc}
                      </p>
                    </div>

                    {/* Bottom Metrics Pill Bar */}
                    <div className="mt-auto grid grid-cols-3 divide-x divide-white/15 bg-[#071B34] px-2.5 py-3.5 text-center rounded-b-[22px]">
                      {cs.metrics.map((m, mIdx) => (
                        <div key={mIdx} className="flex flex-col items-center px-1">
                          <span className="text-[15px] sm:text-[16px] font-bold text-white">
                            {m.value}
                          </span>
                          <span className="mt-0.5 text-[9.5px] sm:text-[10px] text-[#94A3B8] leading-tight text-center">
                            {m.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Slider Arrow Controls at bottom-right */}
              <div className="mt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => scrollSlider("left")}
                  aria-label="Previous Case Study"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[#CBD5E1] bg-white text-[#475569] shadow-sm hover:border-[#94A3B8] hover:bg-[#F8FAFC] transition-all cursor-pointer"
                >
                  <ArrowLeft className="h-4 w-4" strokeWidth={2} />
                </button>
                <button
                  type="button"
                  onClick={() => scrollSlider("right")}
                  aria-label="Next Case Study"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-[#CBD5E1] bg-white text-[#475569] shadow-sm hover:border-[#94A3B8] hover:bg-[#F8FAFC] transition-all cursor-pointer"
                >
                  <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: CTA BANNER (YOUR NEXT OPPORTUNITY) */}
      <section className="relative w-full overflow-hidden bg-[#071322] py-20 lg:py-28 min-h-[440px] flex items-center">
        {/* Background Image */}
        <img
          src="/images/simpel-loop-cta-bg.png"
          alt="Mountain peak sunrise - Simpel Loop opportunity"
          className="absolute inset-0 h-full w-full object-cover object-right md:object-center pointer-events-none select-none"
        />

        {/* Mobile subtle darkening overlay */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-transparent md:hidden" />

        <div className="relative z-10 mx-auto max-w-[1440px] w-full px-6 lg:px-[80px]">
          <div className="max-w-[720px] flex flex-col items-start">
            {/* Tag / Eyebrow */}
            <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
              YOUR NEXT OPPORTUNITY
            </p>

            {/* Headline */}
            <h2 className="text-[28px] sm:text-[34px] lg:text-[38px] font-bold text-white tracking-[-0.02em] leading-[1.2] mt-3">
              Let’s create what’s next, together.
            </h2>

            {/* Paragraph */}
            <p className="text-[13.5px] sm:text-[15px] leading-[23px] sm:leading-[25px] text-[#94A3B8] mt-3.5 max-w-[680px]">
              Talk to our team to explore how Simpel LOOP™ can help you turn opportunities into
              <span className="block">lasting business impact.</span>
            </p>

            {/* CTA Button with Cyan Glow */}
            <div className="mt-7 sm:mt-8">
              <button
                type="button"
                onClick={openAgent}
                className="group relative inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3 text-[14px] font-semibold text-[#0B132B] shadow-[0_0_30px_rgba(32,153,236,0.65),0_0_12px_rgba(32,153,236,0.4)] transition-all duration-300 hover:scale-105 hover:bg-white active:scale-95 cursor-pointer"
              >
                <span>Talk to Our Team</span>
                <ArrowRight
                  className="h-4 w-4 text-[#0B132B] transition-transform group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </motion.div>
  );
};

export default SimpelLoop;
